import React from 'react';
import type { BoxProps } from '@withjoy/joykit';
import type { HotelRoomBlockPricingValues, StandardHotelPricingValues } from '@shared/types/Accommodations.types';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { DiscountBadge, DiscountBadgeWithTooltip } from './DiscountBadge';

type AccommodationsDiscountTooltipPayload = Readonly<{
  minimumDiscountAmountToShow: number;
}>;

const isAccommodationsDiscountTooltipPayload = (value: unknown): value is AccommodationsDiscountTooltipPayload => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as { minimumDiscountAmountToShow?: unknown };
  return typeof payload.minimumDiscountAmountToShow === 'number';
};

/**
 * The `% off` badge for a rate, with its price-breakdown tooltip when the rate qualifies for one.
 *
 * Shared rather than inlined because the badge does not always sit in the price column: the
 * cash-fund tile moves it onto the stars row. Rebuilding it there dropped the tooltip — and with
 * it the customer-facing original-price and Joy-discount breakdown — for every discounted fund
 * listing. One owner for the flag read, the threshold and the choice of badge keeps the two
 * placements honest.
 *
 * `badgeProps` restyles the badge without touching either shape: the fund comp inverts it to a
 * light pill, and every other tile keeps the solid green square.
 *
 * `skip` matters more than it looks. Reading a remote flag is what Amplitude counts as an exposure,
 * and the exposure queue does not dedupe — so a tile that calls this AND renders a `CostBreakdown`
 * banks two exposures for one listing and inflates the experiment denominator. Exactly one of the
 * two must own the read: whichever one actually places the badge.
 *
 * Returns null when there is no percentage to quote.
 */
export const useDiscountBadge = (
  pricingInfo: StandardHotelPricingValues | HotelRoomBlockPricingValues | null | undefined,
  badgeProps?: BoxProps,
  options?: Readonly<{ skip?: boolean }>,
): React.ReactElement | null => {
  const { tq } = useTranslation('accommodationsConsole');
  const { discountPercentage, nightlyBaseRateString, strikeoutNightlyBaseRateString, discountAmountString, discountAmount } = pricingInfo ?? {};

  // Read the flag ONLY when a badge can actually appear. `!discountPercentage` covers both cases
  // that cannot show one: pricing still loading, and a rate with no discount. Reading either way
  // banks an exposure for a couple who never had the chance to see the variant.
  const { payload } = useFeatureValue('accommodationsDiscountTooltipEnabled', { skip: options?.skip || !discountPercentage });

  const minimumDiscountAmountToShow = isAccommodationsDiscountTooltipPayload(payload) ? payload.minimumDiscountAmountToShow : 0;

  if (!discountPercentage) return null;

  const label = tq`${discountPercentage}% off`;

  // Spelled out rather than hoisted into a boolean, so the compiler narrows the three strings the
  // tooltip needs. A boolean would leave them optional and force casts that hide a shape change.
  if (payload && discountAmount && discountAmount > minimumDiscountAmountToShow && nightlyBaseRateString && strikeoutNightlyBaseRateString && discountAmountString) {
    return (
      <DiscountBadgeWithTooltip
        totalPrice={nightlyBaseRateString}
        discountAmount={discountAmountString}
        originalPrice={strikeoutNightlyBaseRateString}
        label={label}
        badgeProps={badgeProps}
      />
    );
  }

  return (
    <DiscountBadge
      label={label}
      {...badgeProps}
    />
  );
};
